import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { formatDuration } from "JS/Helpers";
import { AppTypography } from "JS/React/Components/AppTypography";
import { RefreshButton } from "JS/React/Components/MediaPlayer/RefreshButton";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";
import { useEffect, useState } from "react";
import { OnProgressProps } from "react-player/base";

const StyledRoot = styled("div")(({ theme }) => ({
    position: "absolute",
    width: "100%",
    height: "100%",
    padding: theme.spacing(1),
    background: "rgba(0,0,0,0.5)",
    opacity: 0,
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "all 0.5s ease",
    "&.visible": {
        opacity: 1,
    },
}));

const StyledLayoutIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "1.1rem",
    cursor: "pointer",
    zIndex: 100,
    color: theme.colors.white,
}));

const StyledActionIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "1.4rem",
    cursor: "pointer",
    zIndex: 100,
    color: theme.colors.white,
}));

const StyledProgressText = styled(AppTypography)(({ theme }) => ({
    color: theme.colors.white,
    fontSize: "0.8rem",
}));

const StyledTitle = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    WebkitLineClamp: 1,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    maxWidth: "75%",
}));

export const MiniPlayerOverlay = (props: MiniPlayerOverlayProps) => {
    const { getProgressString } = useHandlers(props);

    const {
        playing,
        playerProgress,
        playerDuration,
        disableNavBtns,
        title,
        showButtons,
        isError,
        loading,
        onClosePlayer,
        onExpandClick,
        onForwardClick,
        onBackwardClick,
        onPlayPauseClick,
    } = props;

    const [showOverlay, setShowOverlay] = useState(true);

    useEffect(() => {
        const onVisibilityChange = () => {
            setShowOverlay(true);
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () =>
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (showOverlay) setShowOverlay(false);
        }, 5000);

        return () => {
            clearTimeout(timeout);
        };
    }, [showOverlay]);

    const enableOverlay = showOverlay && showButtons;

    return (
        <StyledRoot
            onTouchStart={(e) => {
                setTimeout(() => {
                    setShowOverlay(!showOverlay);
                }, 500);
            }}
            onMouseLeave={() => {
                setShowOverlay(false);
            }}
            className={clsx(enableOverlay && "visible")}
        >
            <>
                <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                >
                    {!isError && (
                        <IconButton
                            sx={!enableOverlay ? { pointerEvents: "none" } : {}}
                            onClick={onExpandClick}
                        >
                            <StyledLayoutIcon
                                id="expand"
                                icon={["fas", "up-right-from-square"]}
                            />
                        </IconButton>
                    )}
                    <StyledTitle
                        overflow="hidden"
                        whiteSpace="pre-line"
                        textOverflow="ellipsis"
                        color="white"
                    >
                        {title}
                    </StyledTitle>

                    <Box id="closemax" display={"flex"} gap={2}>
                        <IconButton
                            sx={!enableOverlay ? { pointerEvents: "none" } : {}}
                            onClick={onClosePlayer}
                            onTouchStart={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <StyledActionIcon icon={["fas", "xmark"]} />
                        </IconButton>
                    </Box>
                </Box>
                {isError && (
                    <Box
                        display={"flex"}
                        flexDirection={"column"}
                        textAlign={"center"}
                    >
                        <Box
                            sx={!enableOverlay ? { pointerEvents: "none" } : {}}
                        >
                            <RefreshButton isMini />
                        </Box>
                        <StyledProgressText variant="body1">
                            Something went wrong. Please try again
                        </StyledProgressText>
                    </Box>
                )}
                {!isError && !loading && (
                    <Box display={"flex"} justifyContent={"space-evenly"}>
                        <IconButton
                            id="previous"
                            disabled={disableNavBtns || !enableOverlay}
                            onTouchStart={(e) => {
                                e.stopPropagation();
                                onBackwardClick();
                            }}
                            sx={
                                !onBackwardClick || disableNavBtns
                                    ? { visibility: "hidden" }
                                    : {}
                            }
                        >
                            <StyledLayoutIcon icon={["fas", "backward-step"]} />
                        </IconButton>
                        <IconButton
                            id="playpause"
                            disabled={!enableOverlay}
                            onTouchStart={(e) => {
                                e.stopPropagation();
                                onPlayPauseClick();
                            }}
                        >
                            <StyledLayoutIcon
                                icon={["fas", playing ? "pause" : "play"]}
                            />
                        </IconButton>
                        <IconButton
                            id="next"
                            disabled={disableNavBtns || !enableOverlay}
                            onTouchStart={(e) => {
                                e.stopPropagation();
                                onForwardClick();
                            }}
                            sx={
                                !onForwardClick || disableNavBtns
                                    ? { visibility: "hidden" }
                                    : {}
                            }
                        >
                            <StyledLayoutIcon icon={["fas", "forward-step"]} />
                        </IconButton>
                    </Box>
                )}
                {!isError && loading && (
                    <Box display={"flex"} justifyContent={"space-evenly"}>
                        <AppCircularProgress />
                    </Box>
                )}
                <Box>
                    {playerProgress && playerDuration && !isError && (
                        <StyledProgressText variant="body1">
                            {getProgressString()}
                        </StyledProgressText>
                    )}
                </Box>
            </>
        </StyledRoot>
    );
};

const useHandlers = (props: MiniPlayerOverlayProps) => {
    const { playerDuration, playerProgress } = props;

    const getProgressString = () => {
        if (playerDuration && playerProgress) {
            return `${formatDuration(playerProgress.playedSeconds)} /
                ${formatDuration(playerDuration)}`;
        }

        return "0 / 0";
    };

    return { getProgressString };
};

export interface MiniPlayerOverlayProps
    extends React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    > {
    isError: boolean;
    playerProgress: OnProgressProps;
    playerDuration: number;
    playing: boolean;
    disableNavBtns: boolean;
    title?: string;
    showButtons?: boolean;
    onClosePlayer: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onExpandClick: () => void;
    onPlayPauseClick: () => void;
    loading?: boolean;
    onForwardClick?: () => void;
    onBackwardClick?: () => void;
}
